import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowLeft, FlaskConical, Loader2, Pin, Play, RotateCcw, Square } from 'lucide-react';
import {
  Component,
  Experiment,
  Gate,
  cancelExperiment,
  getGate,
  listComponents,
  listExperiments,
  pinBaseline,
  retryFailed,
  submitExperiment,
} from '../experiments';

/**
 * The engine console for the experiment plane (DESIGN.md §11.8): submit a candidate, watch it,
 * read its floors, timing and tier-B agreement, compare it with its baseline, read the gate.
 *
 * Native rather than the laptop console's HTML: the portal's CSP forbids inline styles, and a
 * page that has to relax the CSP to render is the wrong trade for a PHI-adjacent product. The same
 * numbers are available as HTML from `scribe-exp console`.
 *
 * Nothing here is clinical. A candidate's result is evidence for a promotion PR (§11.7, W6), never
 * a promotion: no button on this screen changes what the engine ships.
 */

const STATUS_STYLE: Record<Experiment['status'], string> = {
  QUEUED: 'bg-slate-800 border-slate-700 text-slate-300',
  RUNNING: 'bg-cyan-950 border-cyan-800 text-cyan-300',
  DONE: 'bg-emerald-950 border-emerald-800 text-emerald-300',
  PARTIAL: 'bg-amber-950 border-amber-800 text-amber-300',
  CANCELLED: 'bg-rose-950 border-rose-800 text-rose-300',
};
const BAND_STYLE = { green: 'text-emerald-300', amber: 'text-amber-300', fail: 'text-rose-300' } as const;

const fmt = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : Math.abs(v) >= 100 ? v.toFixed(1) : v.toFixed(3);
const csv = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

export const Experiments: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [rows, setRows] = useState<Experiment[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [gate, setGate] = useState<Gate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ component: '', overrides: '', suites: '', datasets: '', devices: '', purpose: 'experiment' });

  const load = useCallback(async () => {
    try {
      setRows(await listExperiments());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load experiments.');
    }
  }, []);
  useEffect(() => {
    void load();
    void listComponents().then(setComponents).catch(() => undefined);
    const timer = window.setInterval(() => void load(), 15000);
    return () => window.clearInterval(timer);
  }, [load]);

  const current = useMemo(() => rows.find((r) => r.id === selected) ?? null, [rows, selected]);
  useEffect(() => {
    setGate(null);
    if (current) void getGate(current.component).then(setGate).catch(() => undefined);
  }, [current?.component, current?.finalized_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      setError(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That action was refused.');
    } finally {
      setBusy(false);
    }
  };

  const submit = () =>
    act(async () => {
      let overrides: Record<string, unknown> | undefined;
      if (form.overrides.trim()) {
        try {
          overrides = JSON.parse(form.overrides);
        } catch {
          throw new Error('Overrides must be JSON, e.g. {"afib": {"DETECTION_THRESHOLD": 0.5}}');
        }
      }
      const created = await submitExperiment({
        component: form.component,
        overrides,
        suites: form.suites ? csv(form.suites) : undefined,
        datasets: form.datasets ? csv(form.datasets) : undefined,
        devices: form.devices ? csv(form.devices) : undefined,
        purpose: form.purpose,
      });
      setSelected(created.id);
    });

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4 text-slate-200">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center space-x-2 text-sm text-slate-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" />
          <span>Back</span>
        </button>
        <h1 className="flex items-center space-x-2 text-lg font-semibold">
          <FlaskConical className="h-5 w-5 text-cyan-400" />
          <span>Experiments</span>
        </h1>
        <span className="text-xs text-slate-500">refreshes every 15 s</span>
      </div>

      {error && (
        <div className="flex items-center space-x-2 rounded-lg border border-rose-800 bg-rose-950 p-3 text-sm text-rose-200">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <section className="rounded-xl border border-slate-800 bg-slate-900 p-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-300">Submit a config candidate</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <label className="text-xs text-slate-400">
            Component
            <select
              value={form.component}
              onChange={(e) => setForm({ ...form, component: e.target.value })}
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 p-2 text-sm"
            >
              <option value="">choose…</option>
              {components.map((c) => (
                <option key={c.name} value={c.name}>{c.name} — {c.tests.join(', ')}</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-400">
            Purpose
            <select
              value={form.purpose}
              onChange={(e) => setForm({ ...form, purpose: e.target.value })}
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 p-2 text-sm"
            >
              <option value="experiment">experiment</option>
              <option value="baseline">baseline (DEFAULT, no overrides)</option>
              <option value="promotion-check">promotion-check</option>
            </select>
          </label>
          <label className="text-xs text-slate-400">
            Suites (optional; <code>real-whole</code>, <code>timing</code> are opt-in)
            <input value={form.suites} onChange={(e) => setForm({ ...form, suites: e.target.value })}
              placeholder="default: what the manifest qualifies"
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 p-2 text-sm" />
          </label>
          <label className="text-xs text-slate-400 md:col-span-2">
            Overrides to EngineConfig.DEFAULT (JSON; empty = DEFAULT)
            <textarea value={form.overrides} onChange={(e) => setForm({ ...form, overrides: e.target.value })}
              rows={3} placeholder='{"afib": {"DETECTION_THRESHOLD": 0.5}}'
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 p-2 font-mono text-xs" />
          </label>
          <div className="space-y-3">
            <label className="block text-xs text-slate-400">
              Datasets (optional)
              <input value={form.datasets} onChange={(e) => setForm({ ...form, datasets: e.target.value })}
                placeholder="e.g. mitdb, afdb"
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 p-2 text-sm" />
            </label>
            <label className="block text-xs text-slate-400">
              Devices (optional)
              <input value={form.devices} onChange={(e) => setForm({ ...form, devices: e.target.value })}
                placeholder="lifesignals_2ch, wellue_1ch"
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 p-2 text-sm" />
            </label>
          </div>
        </div>
        <button onClick={submit} disabled={busy || !form.component}
          className="mt-3 flex items-center space-x-2 rounded-lg border border-cyan-700 px-3 py-1.5 text-sm text-cyan-200 hover:border-cyan-400 disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          <span>Submit</span>
        </button>
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <section className="rounded-xl border border-slate-800 bg-slate-900 lg:col-span-2">
          <h2 className="border-b border-slate-800 p-3 text-sm font-semibold text-slate-300">Recent</h2>
          <ul className="max-h-[70vh] divide-y divide-slate-800 overflow-y-auto">
            {rows.length === 0 && <li className="p-3 text-sm text-slate-500">No experiments yet.</li>}
            {rows.map((e) => (
              <li key={e.id}>
                <button onClick={() => setSelected(e.id)}
                  className={`w-full p-3 text-left text-sm hover:bg-slate-800 ${selected === e.id ? 'bg-slate-800' : ''}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{e.component}</span>
                    <span className={`rounded border px-1.5 py-0.5 text-xs ${STATUS_STYLE[e.status]}`}>{e.status}</span>
                  </div>
                  <div className="mt-1 flex justify-between text-xs text-slate-500">
                    <span>{e.purpose}{Object.keys(e.overrides).length ? ' · candidate' : ' · DEFAULT'}</span>
                    <span>{e.counts.done}/{e.n_units} done{e.counts.failed ? ` · ${e.counts.failed} failed` : ''}</span>
                  </div>
                  <div className="text-xs text-slate-600">{e.created_at?.slice(0, 16).replace('T', ' ')} · {e.id.slice(0, 8)}</div>
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="space-y-4 rounded-xl border border-slate-800 bg-slate-900 p-4 lg:col-span-3">
          {!current ? (
            <p className="text-sm text-slate-500">Choose an experiment.</p>
          ) : (
            <Detail e={current} gate={gate} busy={busy} act={act} />
          )}
        </section>
      </div>
    </div>
  );
};

const Detail: React.FC<{ e: Experiment; gate: Gate | null; busy: boolean; act: (fn: () => Promise<unknown>) => void }> = ({
  e, gate, busy, act,
}) => {
  const s = e.summary ?? {};
  const moved = Object.entries(s.vs_baseline ?? {}).filter(([, d]) => d.direction !== 'same');
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">{e.component}</h2>
          <p className="font-mono text-xs text-slate-500">{e.id} · config {e.config_hash.slice(0, 12)} · git {e.git_sha?.slice(0, 12) ?? '—'}</p>
        </div>
        <div className="flex gap-2">
          {(e.status === 'QUEUED' || e.status === 'RUNNING') && (
            <button disabled={busy} onClick={() => act(() => cancelExperiment(e.id))}
              className="flex items-center space-x-1 rounded-lg border border-rose-800 px-2 py-1 text-xs text-rose-300">
              <Square className="h-3.5 w-3.5" /><span>Cancel</span>
            </button>
          )}
          {e.counts.failed > 0 && (
            <button disabled={busy} onClick={() => act(() => retryFailed(e.id))}
              className="flex items-center space-x-1 rounded-lg border border-amber-800 px-2 py-1 text-xs text-amber-300">
              <RotateCcw className="h-3.5 w-3.5" /><span>Retry failed</span>
            </button>
          )}
          {e.status === 'DONE' && !Object.keys(e.overrides).length && !e.image_tag && (
            <button disabled={busy} onClick={() => act(() => pinBaseline(e.id))}
              className="flex items-center space-x-1 rounded-lg border border-cyan-800 px-2 py-1 text-xs text-cyan-300">
              <Pin className="h-3.5 w-3.5" /><span>Pin as baseline</span>
            </button>
          )}
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-2 text-xs md:grid-cols-5">
        {(['queued', 'running', 'done', 'failed', 'cancelled'] as const).map((k) => (
          <div key={k} className="rounded-lg bg-slate-950 p-2">
            <dt className="text-slate-500">{k}</dt>
            <dd className="text-sm">{e.counts[k]}</dd>
          </div>
        ))}
      </dl>

      <div className="text-xs text-slate-400">
        <div>Measured on: {e.resolved_set.suites.join(', ') || '—'} · {e.resolved_set.datasets.join(', ') || '—'}
          {e.resolved_set.devices.length ? ` · ${e.resolved_set.devices.join(', ')}` : ''}</div>
        {e.resolved_set.ignored.map((i) => <div key={i} className="text-slate-500">not run: {i}</div>)}
        {Object.entries(e.resolved_set.excluded).map(([d, m]) => (
          <div key={d} className="text-slate-500">excluded {d} (trained {m.join(', ')})</div>
        ))}
        {Object.keys(e.overrides).length > 0 && (
          <pre className="mt-2 overflow-x-auto rounded bg-slate-950 p-2 font-mono">{JSON.stringify(e.overrides, null, 2)}</pre>
        )}
      </div>

      {(s.finalize_error || s.abandoned) && (
        <div className="rounded-lg border border-amber-800 bg-amber-950 p-2 text-xs text-amber-200">
          {s.abandoned ? `Abandoned: ${s.abandoned}` : `Finalize failed (retried automatically): ${s.finalize_error}`}
        </div>
      )}

      {s.public && (
        <div>
          <h3 className="mb-1 text-sm font-semibold">
            Public floors{' '}
            <span className={s.floors_pass ? 'text-emerald-300' : 'text-rose-300'}>{s.floors_pass ? 'intact' : 'NOT intact'}</span>
          </h3>
          {s.public_incomplete_suites?.length ? (
            <p className="text-xs text-amber-300">Incomplete suites (units failed): {s.public_incomplete_suites.join(', ')} — these numbers are not the suite's.</p>
          ) : null}
          <div className="max-h-72 overflow-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500"><tr><th className="text-left">metric</th><th>value</th><th>bound</th><th /></tr></thead>
              <tbody>
                {Object.entries(s.public).map(([name, m]) => (
                  <tr key={name} className="border-t border-slate-800">
                    <td className="py-0.5 font-mono">{name}</td>
                    <td className="text-right tabular-nums">{fmt(m.value)}</td>
                    <td className="text-right text-slate-500">{m.floor !== null ? `≥ ${m.floor}` : m.ceiling !== null ? `≤ ${m.ceiling}` : 'not gated'}</td>
                    <td className={m.pass ? 'text-emerald-400' : 'text-rose-400'}>{m.pass ? 'pass' : 'FAIL'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {s.timing && (
        <div className="text-xs">
          <h3 className="mb-1 text-sm font-semibold">7-day timing on the worker</h3>
          {Object.entries(s.timing).map(([d, t]) => (
            <div key={d}>{d}: <span className={BAND_STYLE[t.band]}>{t.minutes.toFixed(2)} min ({t.band})</span></div>
          ))}
        </div>
      )}

      {(s.tierb || s.real_whole) && (
        <div className="grid grid-cols-1 gap-2 text-xs md:grid-cols-2">
          {[['Tier-B windows', s.tierb], ['Whole studies (disagreement report)', s.real_whole]].map(([title, m]) =>
            m ? (
              <div key={title as string}>
                <h3 className="mb-1 text-sm font-semibold">{title as string}</h3>
                {Object.entries(m as Record<string, number>).map(([k, v]) => (
                  <div key={k} className="flex justify-between font-mono"><span>{k}</span><span>{fmt(v)}</span></div>
                ))}
              </div>
            ) : null,
          )}
        </div>
      )}

      {s.vs_baseline && (
        <div className="text-xs">
          <h3 className="mb-1 text-sm font-semibold">
            Against baseline {e.baseline_id?.slice(0, 8)}{' '}
            {s.same_resolved_set === false && <span className="text-amber-300">(measured on a different set — not comparable)</span>}
          </h3>
          {moved.length === 0 ? <p className="text-slate-500">Nothing moved.</p> : (
            <table className="w-full">
              <tbody>
                {moved.map(([k, d]) => (
                  <tr key={k} className="border-t border-slate-800">
                    <td className="font-mono">{k}</td>
                    <td className="text-right tabular-nums">{fmt(d.a)} → {fmt(d.b)}</td>
                    <td className="text-right tabular-nums">{d.delta === null ? d.direction : (d.delta > 0 ? '+' : '') + fmt(d.delta)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {gate && (
        <div className="text-xs">
          <h3 className="mb-1 text-sm font-semibold">Gate — {gate.component} {gate.baseline_id ? `(baseline ${gate.baseline_id.slice(0, 8)})` : '(no baseline pinned)'}</h3>
          <pre className="max-h-64 overflow-auto rounded bg-slate-950 p-2 font-mono">{gate.report}</pre>
        </div>
      )}
      {e.mlflow_run_id && <p className="text-xs text-slate-500">MLflow run {e.mlflow_run_id}{e.mlflow_url ? ` — ${e.mlflow_url}` : ''}</p>}
    </>
  );
};
